import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
    Typography,
    DialogContent,
    DialogActions,
    MenuItem,
    Grid,
    FormControl,
    InputLabel,
    Select,
    TextField,
} from "@material-ui/core";
import DateFnsUtils from "@date-io/date-fns";
import { dateLocale } from "@icarius-localization/strings";
import { MuiPickersUtilsProvider, DatePicker } from "@material-ui/pickers";
import { getLocalizedString } from "@icarius-localization/strings";
import { addDays } from "date-fns";
import {
    dateIsAfterToday,
    formatDate,
    createDateFromDDMMYYYY,
    isBusinessDay,
    nextBusinessDay,
    businessAdd,
} from "@icarius-utils/date";
import CantCreateRequestMessage from "./cantCreateRequestMessage";
import MyVacationsDialog from "@icarius-common/myVacationsDialog";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import ButtonDialogAction from "@icarius-common/buttonDialogAction";
import { requestsIds } from "@icarius-utils/requestsIds";
import useIsSocietyCountry from "@icarius-app/components/wrapper/hooks/useIsSocietyCountry";

const gridStyle = { minHeight: 75 };

const NewVacationContent = (props) => {
    const {
        isForEdit,
        isReadOnly,
        startHolidaysOnNonWorkingDay,
        allowHalfDayHolidays,
        validateDateRange,
        vacationCalculationData,
        editData,
        requestCode,
        recipients,
        hideViewAccountability,
        handleNoRecipient,
        handleRequest,
        handleClose,
    } = props;

    useEffect(() => {
        if (!isReadOnly && !isForEdit && recipients?.vacation?.length === 0) {
            handleNoRecipient();
            handleClose();
        }
    }, [isReadOnly, isForEdit, recipients, handleNoRecipient, handleClose]);

    const isPeru = useIsSocietyCountry("PE");
    const dispatch = useDispatch();
    const [myVacationsDialogIsOpen, setMyVacationsDialogIsOpen] = useState(false);
    const [formData, setFormData] = useState(() => {
        const timePerDay = (() => {
            if (!allowHalfDayHolidays && !isForEdit && !isReadOnly) return "1";
            if (editData?.id_time_per_day) return parseFloat(editData?.id_time_per_day);
            return "";
        })();

        return {
            receptor: recipients?.vacation?.length === 1 ? recipients.vacation[0].code : "", //si tiene un valor solo, inicializar con ese
            type: editData?.id_subtype || (isPeru ? "UV" : ""),
            date: editData?.start_date ? createDateFromDDMMYYYY(editData.start_date) : null,
            timePerDay: timePerDay,
            amountOfDays: editData?.duration_days || "",
            dateEnd: editData?.end_date ? createDateFromDDMMYYYY(editData.end_date) : null,
            description: editData?.comments || "",
        };
    });

    const setFormValue = (value, fieldName) => {
        if (fieldName === "amountOfDays") {
            setFormData((prev) => ({
                ...formData,
                [fieldName]: value,
                dateEnd: calculateEndVacationDate(prev.date, value),
            }));
            return;
        }

        if (fieldName === "type") {
            setFormData({
                ...formData,
                [fieldName]: value,
                dateEnd: null,
                amountOfDays: "",
            });
            return;
        }

        if (fieldName === "date") {
            setFormData((prev) => ({
                ...formData,
                [fieldName]: value,
                dateEnd: calculateEndVacationDate(value, prev.amountOfDays),
            }));
            return;
        }

        setFormData({
            ...formData,
            [fieldName]: value,
        });
    };

    const handleCloseAndReload = (shouldReloadTable) => {
        if (isForEdit && shouldReloadTable) {
            handleClose(true);
        } else {
            handleClose();
        }
    };

    const handleCloseMyVacationsDialog = () => setMyVacationsDialogIsOpen(false);
    const handleOpenMyVacationsDialog = () => setMyVacationsDialogIsOpen(true);

    const calculateNextDay = () => {
        // A partir del siguiente dia
        let i = 1;
        let fechaBase = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() + i);

        return fechaBase;
    };

    const textIsValid = () => {
        if (isForEdit) return formData.description.length > 0 && formData.description.length <= 1024;
        return !formData.description || (formData.description.length >= 0 && formData.description.length <= 1024);
    };

    const dataIsComplete = () => {
        if (!isForEdit && !isReadOnly && !formData.receptor) return false;
        if (
            formData.type &&
            formData.amountOfDays &&
            formData.timePerDay &&
            textIsValid() &&
            dateIsAfterToday(formData.date)
        )
            return true;
        return false;
    };

    const validateAndCreate = () => {
        if (textIsValid() && dataIsComplete()) {
            if (
                !startHolidaysOnNonWorkingDay &&
                !isBusinessDay(formData.date, [1, 2, 3, 4, 5], vacationCalculationData?.holiday_calendar || [])
            ) {
                dispatch(
                    openSnackbarAction({
                        duration: null,
                        msg: getLocalizedString("startDateCannotBeWeekendOrHoliday"),
                        severity: "error",
                    })
                );
                return;
            }

            if (validateDateRange(formData.date, formData.dateEnd)) {
                const request = {
                    code: requestCode,
                    id_type: requestsIds.holidays,
                    id_status: "3",
                    id_subtype: formData.type,
                    id_time_per_day: formData.timePerDay.toString(),
                    duration_days: formData.amountOfDays,
                    start_date: formatDate(formData.date),
                    end_date: formatDate(formData.dateEnd),
                    comments: formData.description,
                    receptor: formData.receptor,
                };

                if (!isForEdit) {
                    delete request.code;
                    delete request.id_status;
                } else {
                    delete request.id_type;
                    delete request.id_subtype;
                    delete request.receptor;
                }

                handleRequest(request);
                if (!isForEdit) {
                    handleCloseAndReload(true);
                }
            } else {
                dispatch(openSnackbarAction({ msg: getLocalizedString("datesCollide"), severity: "error" }));
            }
        } else {
            dispatch(
                openSnackbarAction({ msg: getLocalizedString("completeWithValidInformation"), severity: "error" })
            );
        }
    };

    const createVacationDaysArray = (typeVacation, maxAmount) => {
        let auxArray = [];
        let cont = 1;
        let totalDays;

        if (maxAmount) {
            do {
                auxArray.push({ FldValue: cont, Descr: cont });
                cont++;
            } while (cont <= maxAmount);
        } else {
            switch (typeVacation) {
                case "UV":
                    totalDays = vacationCalculationData.available_legal_vacation_days;
                    if (vacationCalculationData.additional_legal_days) {
                        totalDays += vacationCalculationData.additional_legal_days;
                    }

                    break;
                case "VA":
                    totalDays = vacationCalculationData.available_extra_vacation_days;
                    break;
                default:
                    break;
            }

            for (cont; cont <= totalDays; cont++) {
                auxArray.push({ FldValue: cont, Descr: cont });
            }
        }

        return auxArray;
    };

    const calculateEndVacationDate = (startDate, amountOfDays) => {
        if (startDate && amountOfDays) {
            const wwd = vacationCalculationData.does_workweek_include_saturdays ? [1, 2, 3, 4, 5, 6] : [1, 2, 3, 4, 5];
            const parsedStart = typeof startDate === "string" ? createDateFromDDMMYYYY(startDate) : new Date(startDate);

            if (!vacationCalculationData.do_vacations_include_holidays_and_weekends) {
                let initialDay = parsedStart;
                // tengo que avanzar hasta el proximo dia habil, en caso de que este no lo sea
                if (!isBusinessDay(initialDay, wwd, vacationCalculationData.holiday_calendar)) {
                    initialDay = nextBusinessDay(initialDay, wwd, vacationCalculationData.holiday_calendar);
                }

                return businessAdd(initialDay, amountOfDays - 1, wwd, vacationCalculationData.holiday_calendar);
            } else {
                return addDays(parsedStart, amountOfDays - 1);
            }
        }

        return null;
    };

    const vacacionDaysArray = (() => {
        if (!isForEdit && !isReadOnly) return createVacationDaysArray(formData.type);
        return createVacationDaysArray(formData.type, editData.duration_days);
    })();

    if (!isForEdit && !isReadOnly && !vacationCalculationData.can_user_appeal) return <CantCreateRequestMessage />;

    return (
        <>
            {myVacationsDialogIsOpen && (
                <MyVacationsDialog open={myVacationsDialogIsOpen} handleClose={handleCloseMyVacationsDialog} />
            )}
            <>
                <DialogContent style={{ paddingTop: 0 }}>
                    <Grid container direction="column" justify="center">
                        {!isForEdit && !isReadOnly && !hideViewAccountability && (
                            <Typography
                                className={"whiteText"}
                                variant="subtitle1"
                                style={{ cursor: "pointer", textDecoration: "underline", zIndex: 100 }}
                                onClick={handleOpenMyVacationsDialog}
                                gutterBottom
                            >
                                {getLocalizedString("viewVacationAccountability")}
                            </Typography>
                        )}
                        {!isForEdit && !isReadOnly && (
                            <Grid container item xs={12} alignItems="center" style={gridStyle}>
                                <FormControl style={{ width: "100%" }}>
                                    <InputLabel required id={`label-receptor`}>
                                        {getLocalizedString("requestReceptor")}
                                    </InputLabel>
                                    <Select
                                        disabled={isForEdit || isReadOnly}
                                        value={formData.receptor}
                                        labelId={`label-receptor`}
                                        id={`select-receptor`}
                                        onChange={(e) => setFormValue(e.target.value, "receptor")}
                                        margin={"none"}
                                    >
                                        {recipients.vacation.map((item) => (
                                            <MenuItem className={"whiteText"} key={item.code} value={item.code}>
                                                {item.value}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}
                        {!isPeru && (
                            <Grid container item xs={12} alignItems="center" style={gridStyle}>
                                <FormControl style={{ width: "100%" }}>
                                    <InputLabel required={!isReadOnly} id={`label-type`}>
                                        {getLocalizedString("vacationsType")}
                                    </InputLabel>
                                    <Select
                                        disabled={isForEdit || isReadOnly}
                                        value={formData.type}
                                        labelId={`label-type`}
                                        id={`select-type`}
                                        onChange={(e) => setFormValue(e.target.value, "type")}
                                        margin={"none"}
                                    >
                                        {[
                                            { FldValue: "UV", Descr: getLocalizedString("legalVacation") },
                                            { FldValue: "VA", Descr: getLocalizedString("additionalVacation") },
                                        ].map((item) => (
                                            <MenuItem className={"whiteText"} key={item.FldValue} value={item.FldValue}>
                                                {item.Descr}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}
                        <Grid container item xs={12} alignItems="center" style={{ marginTop: 10 }}>
                            <Grid container item xs={12} sm={6} alignItems="flex-start" style={gridStyle}>
                                <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
                                    <DatePicker
                                        required={!isReadOnly}
                                        disabled={isReadOnly}
                                        label={getLocalizedString("startDate")}
                                        clearable
                                        clearLabel={getLocalizedString("clear")}
                                        cancelLabel={getLocalizedString("cancel")}
                                        okLabel={getLocalizedString("ok")}
                                        disablePast={!isForEdit}
                                        minDate={!isReadOnly && calculateNextDay()}
                                        format="dd/MM/yyyy"
                                        margin="none"
                                        invalidDateMessage=""
                                        minDateMessage=""
                                        maxDateMessage=""
                                        value={formData.date}
                                        onChange={(e) => setFormValue(e, "date")}
                                        fullWidth
                                    />
                                </MuiPickersUtilsProvider>
                                {!isReadOnly && (
                                    <Typography
                                        className={!dateIsAfterToday(formData.date) ? "errorColor" : "whiteText"}
                                        variant="caption"
                                        gutterBottom
                                    >
                                        {getLocalizedString("startDateValidation")}
                                    </Typography>
                                )}
                            </Grid>
                            <Grid
                                container
                                item
                                xs={12}
                                sm={6}
                                alignItems="flex-start"
                                className="secondFieldPadding"
                                style={gridStyle}
                            >
                                <FormControl style={{ width: "100%" }}>
                                    <InputLabel required={!isReadOnly} id={`label-timePerDay`}>
                                        {getLocalizedString("timePerDay")}
                                    </InputLabel>
                                    <Select
                                        disabled={isReadOnly || !allowHalfDayHolidays}
                                        value={formData.timePerDay}
                                        labelId={`label-timePerDay`}
                                        id={`select-timePerDay`}
                                        onChange={(e) => setFormValue(e.target.value, "timePerDay")}
                                        margin={"none"}
                                    >
                                        {[
                                            { FldValue: "0.5", Descr: getLocalizedString("halfDay") },
                                            { FldValue: "1", Descr: getLocalizedString("fullDay") },
                                        ].map((item) => (
                                            <MenuItem className={"whiteText"} key={item.FldValue} value={item.FldValue}>
                                                {item.Descr}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                        <Grid container item xs={12} alignItems="center">
                            <Grid container item xs={12} sm={6} alignItems="center" style={gridStyle}>
                                <FormControl style={{ width: "100%" }}>
                                    <InputLabel required={!isReadOnly} id={`label-amountOfDays`}>
                                        {getLocalizedString("amountOfDays")}
                                    </InputLabel>
                                    <Select
                                        disabled={isReadOnly}
                                        value={formData.amountOfDays}
                                        labelId={`label-amountOfDays`}
                                        id={`select-amountOfDays`}
                                        onChange={(e) => setFormValue(e.target.value, "amountOfDays")}
                                        margin={"none"}
                                    >
                                        {vacacionDaysArray.map((item) => (
                                            <MenuItem className={"whiteText"} key={item.FldValue} value={item.FldValue}>
                                                {item.Descr}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid container item xs={12} sm={6} className="secondFieldPadding">
                                <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
                                    <DatePicker
                                        disabled
                                        label={getLocalizedString("endDate")}
                                        format="dd/MM/yyyy"
                                        margin="none"
                                        value={formData.dateEnd}
                                        fullWidth
                                    />
                                </MuiPickersUtilsProvider>
                            </Grid>
                        </Grid>
                        {!isForEdit && !isReadOnly && formData.type === "UV" && (
                            <Grid container item style={{ paddingBottom: 15 }}>
                                <Typography
                                    style={{ paddingTop: 5, color: "red", fontWeight: 700 }}
                                    variant="caption"
                                    gutterBottom
                                >
                                    {`Saldo disponible en la cuenta corriente: ${vacationCalculationData.available_legal_vacation_days}`}
                                </Typography>
                            </Grid>
                        )}
                        {((isForEdit && !isReadOnly) || isReadOnly) &&
                            formData.type === "UV" &&
                            formData.amountOfDays > vacationCalculationData.available_legal_vacation_days && (
                                <Grid container item style={{ paddingBottom: 15 }}>
                                    <Typography
                                        style={{ paddingTop: 5, color: "red", fontWeight: 700 }}
                                        variant="caption"
                                        gutterBottom
                                    >
                                        {getLocalizedString(
                                            formData.amountOfDays === 1
                                                ? "requestedDayIsBiggerThanAvailable"
                                                : "requestedDaysAreBiggerThanAvailable",
                                            {
                                                amount:
                                                    formData.amountOfDays -
                                                    vacationCalculationData.available_legal_vacation_days,
                                                requested: formData.amountOfDays,
                                            }
                                        )}
                                    </Typography>
                                </Grid>
                            )}
                        {isForEdit && !isReadOnly && (
                            <Grid container item style={{ paddingBottom: 15 }}>
                                <Typography
                                    style={{ paddingTop: 5 }}
                                    className={"whiteText"}
                                    variant="caption"
                                    gutterBottom
                                >
                                    {getLocalizedString("howToSelectMoreDays")}
                                </Typography>
                            </Grid>
                        )}
                        <Grid container item xs={12} alignItems="center" style={{ marginTop: 10 }}>
                            <TextField
                                required={isForEdit && !isReadOnly}
                                label={getLocalizedString("detail")}
                                disabled={isReadOnly}
                                margin={"none"}
                                value={formData.description}
                                onChange={(e) => setFormValue(e.target.value.toString().slice(0, 1024), "description")}
                                multiline={true}
                                rows={1}
                                rowsMax={3}
                                fullWidth
                            />
                            {!isReadOnly && (
                                <Typography
                                    style={{ paddingTop: 5 }}
                                    className={!textIsValid() ? "errorColor" : "whiteText"}
                                    variant="caption"
                                    gutterBottom
                                >
                                    {!isForEdit && `${getLocalizedString("optionalField")}. `}
                                    {`${getLocalizedString("currentChar")} ${
                                        formData.description ? formData.description.length : 0
                                    }. ${getLocalizedString("maxCharRequests")}.`}
                                </Typography>
                            )}
                        </Grid>
                    </Grid>
                </DialogContent>
                {isReadOnly && <div style={{ marginTop: 32 }} />}
                {!isReadOnly && (
                    <DialogActions>
                        <ButtonDialogAction onClick={handleCloseAndReload} text={getLocalizedString("disagree")} />
                        <ButtonDialogAction onClick={validateAndCreate} text={getLocalizedString("agree")} isAccept />
                    </DialogActions>
                )}
            </>
        </>
    );
};

export default NewVacationContent;
